import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/services/api";
import {
  Copy,
  Share2,
  TrendingUp,
  Package,
  DollarSign,
  Eye,
  CheckCircle2,
  Clock,
  ChevronRight,
  Sparkles,
  Award,
  Instagram,
  Facebook,
  MessageCircle,
  Music2,
  ExternalLink,
  RefreshCw,
  Wallet,
  ShoppingBag,
  Users,
  Target,
  LogOut,
  User,
} from "lucide-react";

const LEVEL_CONFIG = {
  embajador: { name: "Embajador", color: "from-gray-400 to-gray-500", next: "oro", nextSales: 10 },
  oro: { name: "Embajador Oro", color: "from-amber-400 to-amber-600", next: "premium", nextSales: 50 },
  premium: { name: "Embajador Premium", color: "from-purple-400 to-purple-600", next: "elite", nextSales: 100 },
  elite: { name: "Embajador Elite", color: "from-rose-400 to-rose-600", next: null, nextSales: null },
};

export default function EmbajadorDashboard() {
  const navigate = useNavigate();
  const [embajador, setEmbajador] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, statsRes] = await Promise.all([
        api.get("/embajadores/me"),
        api.get("/embajadores/me/stats")
      ]);
      setEmbajador(profileRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("embajador");
        navigate("/embajador/login");
      }
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("embajador");
    localStorage.removeItem("userRole");
    navigate("/embajador/login");
    toast.success("Sesión cerrada");
  };

  const affiliateLink = embajador ? `https://barujshefa.com/s/${embajador.codigo_afiliado}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(affiliateLink);
    toast.success("¡Link copiado al portapapeles!");
  };

  const shareWhatsApp = () => {
    const text = `¡Hola! 🥐 Te recomiendo los productos artesanales de Baruj Shefa. Usa mi link para hacer tu pedido: ${affiliateLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(affiliateLink)}`, "_blank");
  };

  const shareInstagram = () => {
    navigator.clipboard.writeText(affiliateLink);
    toast.success("Link copiado - Pégalo en tu historia de Instagram");
  };

  const shareTikTok = () => {
    navigator.clipboard.writeText(affiliateLink);
    toast.success("Link copiado - Agrégalo a tu bio de TikTok");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando tu dashboard...</p>
        </div>
      </div>
    );
  }

  const levelConfig = LEVEL_CONFIG[embajador?.nivel || "embajador"];
  const progressToNextLevel = levelConfig.nextSales 
    ? Math.min((stats?.total_orders || 0) / levelConfig.nextSales * 100, 100)
    : 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold">{embajador?.nombre?.charAt(0)}</span>
              </div>
              <div>
                <p className="text-white/80 text-sm">¡Hola!</p>
                <h1 className="text-xl font-bold">{embajador?.nombre?.split(" ")[0]}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => navigate("/embajador/perfil")}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
              >
                <User className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleRefresh}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Level Badge */}
          <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${levelConfig.color} px-4 py-2 rounded-full text-sm font-semibold shadow-lg`}>
            <Award className="w-4 h-4" />
            {levelConfig.name}
          </div>

          {/* Progress to next level */}
          {levelConfig.next && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-white/80 text-xs mb-1">
                <span>Progreso a {LEVEL_CONFIG[levelConfig.next].name}</span>
                <span>{stats?.total_orders || 0} / {levelConfig.nextSales} pedidos</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${progressToNextLevel}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Affiliate Link Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-amber-100">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h2 className="font-bold text-gray-900">Tu Link de Embajador</h2>
          </div>
          
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-600 mb-2">Comparte este link único:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white px-4 py-3 rounded-lg text-amber-700 font-mono text-sm border border-amber-200 truncate">
                {affiliateLink}
              </code>
              <Button
                onClick={copyLink}
                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-3 h-auto"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={shareWhatsApp}
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl font-medium transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="hidden sm:inline">WhatsApp</span>
            </button>
            <button
              onClick={shareFacebook}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-xl font-medium transition-colors"
            >
              <Facebook className="w-5 h-5" />
              <span className="hidden sm:inline">Facebook</span>
            </button>
            <button
              onClick={shareInstagram}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 px-4 rounded-xl font-medium transition-colors"
            >
              <Instagram className="w-5 h-5" />
              <span className="hidden sm:inline">Instagram</span>
            </button>
            <button
              onClick={shareTikTok}
              className="flex items-center justify-center gap-2 bg-black hover:bg-gray-800 text-white py-3 px-4 rounded-xl font-medium transition-colors"
            >
              <Music2 className="w-5 h-5" />
              <span className="hidden sm:inline">TikTok</span>
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats?.total_clicks || 0}</p>
            <p className="text-sm text-gray-500">Clicks</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats?.total_orders || 0}</p>
            <p className="text-sm text-gray-500">Pedidos</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats?.orders_delivered || 0}</p>
            <p className="text-sm text-gray-500">Entregados</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats?.conversion_rate || 0}%</p>
            <p className="text-sm text-gray-500">Conversión</p>
          </div>
        </div>

        {/* Commissions Card */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-xl mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="w-5 h-5" />
            <h2 className="font-bold">Tus Comisiones</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-sm mb-1">Disponible</p>
              <p className="text-3xl font-bold">{formatCurrency(stats?.commission_available || 0)}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-white/70 text-sm mb-1">Total pagado</p>
              <p className="text-3xl font-bold">{formatCurrency(stats?.commission_paid || 0)}</p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">Pendiente de confirmación</p>
              <p className="font-semibold">{formatCurrency(stats?.commission_pending || 0)}</p>
            </div>
            <Button
              variant="secondary"
              className="bg-white text-green-600 hover:bg-white/90"
              onClick={() => navigate("/embajador/comisiones")}
            >
              Ver historial
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Quick Stats for this month */}
        <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900">Este mes</h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {new Date().toLocaleDateString("es-CO", { month: "long", year: "numeric" })}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-50 rounded-xl p-4">
              <p className="text-amber-800 text-sm">Pedidos</p>
              <p className="text-2xl font-bold text-amber-900">{stats?.current_month_sales || 0}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-green-800 text-sm">Comisión</p>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(stats?.current_month_commission || 0)}</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => navigate("/embajador/pedidos")}
            className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 text-left hover:shadow-lg transition-shadow"
          >
            <Package className="w-8 h-8 text-purple-500 mb-3" />
            <h3 className="font-semibold text-gray-900">Mis Pedidos</h3>
            <p className="text-sm text-gray-500">Ver pedidos generados</p>
          </button>

          <button
            onClick={() => navigate("/embajador/contenido")}
            className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 text-left hover:shadow-lg transition-shadow"
          >
            <Share2 className="w-8 h-8 text-pink-500 mb-3" />
            <h3 className="font-semibold text-gray-900">Contenido</h3>
            <p className="text-sm text-gray-500">Material para compartir</p>
          </button>
        </div>
      </div>
    </div>
  );
}
